/**
 * Action item generator for Radar.
 * Produces prioritized fix recommendations based on audit results.
 */

import type { ToolKey, ToolState, ActionItem } from '@/types/radar'
import {
  isCrawlRetrievalBot,
  type CrawlBotResult,
} from '@/lib/tools/crawl-bots'
import { sortActions, unrankedPriority } from './action-priority'
import { classifyFindingType } from './finding-type'
import { hasUsableScore } from './tool-scores'
import { isBusinessIdentityType } from './schema-checker'
import { readAnswerEngineSources } from './answer-engine-result'

/* eslint-disable @typescript-eslint/no-explicit-any */

export function generateActions(
  tools: Record<ToolKey, ToolState>
): ActionItem[] {
  const actions: ActionItem[] = []
  let id = 0
  const add = (a: Omit<ActionItem, 'id' | 'priority'>) => {
    actions.push({ ...a, id: `action-${id++}`, priority: id })
  }

  const crawl = tools.crawl.data
  const robots = tools.robots.data
  const llms = tools.llms.data
  const readiness = tools.readiness.data
  const citation = tools.citation.data
  const reddit = tools.reddit.data
  const aeo = tools.aeo.data
  const answer = tools.answer.data
  const scoredAnswer = hasUsableScore(tools.answer) ? answer : null

  // Crawl-based actions
  if (crawl) {
    const blocked =
      crawl.botAccess?.filter(
        (b: CrawlBotResult) => !b.allowed && isCrawlRetrievalBot(b.bot)
      ) || []
    if (blocked.length > 0)
      add({
        category: 'crawlability',
        title: `Unblock ${blocked.length} AI browsing bot${blocked.length > 1 ? 's' : ''} (${blocked.map((b: CrawlBotResult) => b.bot).join(', ')})`,
        why: 'AI browsing bots retrieve your content to include in AI-generated answers. Blocking them means your site is invisible to those platforms.',
        how: 'Edit your robots.txt: remove Disallow rules for these user-agents, or add explicit Allow: / rules. Test with the Crawl Check tool after changes.',
        effort: 'low',
        impact: 'high',
        source: 'crawl',
        methodology: 'GEO',
        relatedUrl: 'https://www.pixelmojo.io/services/geo',
        rubricRef: { tool: 'crawl', category: 'Bot Access' },
        // One action covers every blocked browse bot, so it claims the gap in
        // proportion to how many it unblocks.
        rubricWeight: blocked.length,
      })

    if (crawl.structuredData && !crawl.structuredData.found)
      add({
        category: 'structured-data',
        title: 'Add JSON-LD structured data to your pages',
        why: 'Structured data helps AI models understand your content type (Article, Product, Organization), authorship, and relationships. Without it, AI responses about your site lack context.',
        how: "Add JSON-LD script tags with Schema.org markup. Start with Organization (homepage), Article (blog posts), and BreadcrumbList (navigation). Use Google's Rich Results Test to validate.",
        effort: 'medium',
        impact: 'high',
        source: 'crawl',
        methodology: 'GEO',
        relatedUrl:
          'https://www.pixelmojo.io/blogs/what-is-geo-generative-engine-optimization-complete-guide',
        rubricRef: { tool: 'crawl', category: 'Structured Data' },
      })

    if (crawl.llmsTxt && !crawl.llmsTxt.exists)
      add({
        category: 'llm-communication',
        title: 'Create an llms.txt file',
        why: 'llms.txt is an emerging standard that gives AI models structured context about your business, products, and preferred citation format. Without it, AI models guess based on page content alone.',
        how: 'Create /llms.txt at your domain root. Include sections for company overview, products/services, key URLs, and use policy. Reference the llms.txt specification.',
        effort: 'low',
        impact: 'medium',
        source: 'crawl',
        methodology: 'GEO',
        relatedUrl:
          'https://www.pixelmojo.io/blogs/what-is-llms-txt-complete-guide',
        rubricRef: { tool: 'crawl', category: 'llms.txt' },
        // One fix, two tools. The llms-validator generator cannot emit
        // anything until the file exists, so this action also carries that
        // tool's minimal-valid baseline and unlocks the rest of its rubric.
        // Attribution stays per-tool: crawl gets its own points, llms gets the
        // baseline, and the aggregate counts the action once.
        rubricFoundational: { tool: 'llms' },
      })

    if (crawl.contentAccess && !crawl.contentAccess.isServerRendered)
      add({
        category: 'crawlability',
        title: 'Enable server-side rendering for key pages',
        why: "Client-rendered content (React SPAs without SSR) is often invisible to AI crawlers that don't execute JavaScript. Your content may not be indexable.",
        how: 'Use Next.js server components, or ensure critical content renders on the server. Verify by viewing page source (not DevTools) to confirm HTML contains your content.',
        effort: 'high',
        impact: 'high',
        source: 'crawl',
        rubricRef: { tool: 'crawl', category: 'Content Quality' },
      })

    if (crawl.robotsTxt && !crawl.robotsTxt.hasSitemap)
      add({
        category: 'crawlability',
        title: 'Add sitemap directive to robots.txt',
        why: 'A sitemap declaration in robots.txt helps AI and search bots discover all your pages efficiently, especially deep content.',
        how: 'Add "Sitemap: https://yourdomain.com/sitemap.xml" to your robots.txt. Ensure your sitemap is up-to-date and includes all public pages.',
        effort: 'low',
        impact: 'low',
        source: 'crawl',
        // robots-analyzer independently parses the same file and awards
        // "File Structure" points for a Sitemap directive, so this one edit
        // is measured by both tools.
        rubricRef: [
          { tool: 'crawl', category: 'robots.txt Rules' },
          { tool: 'robots', category: 'File Structure' },
        ],
      })
  }

  // Robots-based actions
  if (robots) {
    if (robots.syntaxIssues?.length > 0) {
      const errors = robots.syntaxIssues.filter(
        (s: any) => s.severity === 'error'
      )
      if (errors.length > 0)
        add({
          category: 'crawlability',
          title: `Fix ${errors.length} robots.txt syntax error${errors.length > 1 ? 's' : ''}`,
          why: 'Invalid robots.txt syntax causes unpredictable bot behavior. Some bots may ignore your rules entirely, others may over-block.',
          how: `Fix these issues: ${errors
            .slice(0, 3)
            .map((e: any) => `Line ${e.line}: ${e.message}`)
            .join('; ')}. Validate with the Robots.txt Analyzer after fixing.`,
          effort: 'low',
          impact: 'medium',
          source: 'robots',
          rubricRef: { tool: 'robots', category: 'Best Practices' },
          rubricWeight: errors.length,
        })
    }
    if (robots.suggestedSnippet)
      add({
        category: 'crawlability',
        title: 'Apply suggested robots.txt improvements',
        why: 'Your robots.txt is missing rules for modern AI bots. Adding explicit rules gives you control over which AI systems can access your content.',
        how: `Add the following to your robots.txt:\n${robots.suggestedSnippet.slice(0, 200)}`,
        effort: 'low',
        impact: 'medium',
        source: 'robots',
        // The snippet adds explicit per-bot rules, which is exactly what
        // "AI Bot Coverage" scores (points per named AI crawler). Both
        // categories are measured independently by robots-analyzer from the
        // file it fetched itself, so one fix legitimately moves both.
        rubricRef: [
          { tool: 'robots', category: 'AI Policy Clarity' },
          { tool: 'robots', category: 'AI Bot Coverage' },
        ],
      })
  }

  // llms.txt-based actions
  if (llms) {
    if (llms.llmsTxtFound && llms.overallScore < 60) {
      const weakAreas =
        llms.scoreBreakdown
          ?.filter((s: any) => s.score < s.maxScore * 0.5)
          .map((s: any) => s.category) || []
      if (weakAreas.length > 0)
        add({
          category: 'llm-communication',
          title: `Improve llms.txt: ${weakAreas.slice(0, 2).join(', ')}`,
          why: `Your llms.txt exists but scored ${llms.overallScore}/100. Weak areas limit how well AI models understand your business.`,
          how: 'Add more detail to weak sections. Ensure entity definitions (using "X is a..." format), internal links to key pages, and a clear use policy section.',
          effort: 'medium',
          impact: 'medium',
          source: 'llms',
          // weakAreas are llms-validator breakdown category names, so the
          // weakest one is a real rubric pointer rather than a guess.
          rubricRef: { tool: 'llms', category: weakAreas[0] },
        })
    }
    if (llms.llmsTxtFound && !llms.llmsFullTxtFound)
      add({
        category: 'llm-communication',
        title: 'Create llms-full.txt for detailed context',
        why: 'llms-full.txt provides expanded information for AI models that want deeper understanding. It complements the concise llms.txt.',
        how: 'Create /llms-full.txt with expanded descriptions, scoring methodologies, detailed service descriptions, and comprehensive URL listings.',
        effort: 'medium',
        impact: 'low',
        source: 'llms',
        rubricRef: { tool: 'llms', category: 'Completeness' },
      })
  }

  // Readiness-based actions
  if (readiness?.categories) {
    const cats = readiness.categories
    if (cats.structuredData?.score < cats.structuredData?.maxScore * 0.5)
      add({
        category: 'structured-data',
        title: 'Improve structured data coverage',
        why: `Structured data scored ${cats.structuredData.score}/${cats.structuredData.maxScore}. AI models rely on Schema.org markup to understand page context.`,
        how: 'Add JSON-LD for Organization, WebSite, and relevant content types. Implement BreadcrumbList for navigation context. Add FAQPage where applicable.',
        effort: 'medium',
        impact: 'high',
        source: 'readiness',
        rubricRef: { tool: 'readiness', category: 'structuredData' },
      })
    if (
      cats.crossSignalReadiness?.score <
      cats.crossSignalReadiness?.maxScore * 0.5
    )
      add({
        category: 'structured-data',
        title: 'Improve cross-signal consistency',
        why: 'Your metadata signals are inconsistent across different formats (meta tags, structured data, llms.txt). AI models weight consistent signals higher.',
        how: 'Ensure your title, description, and key entity information match across meta tags, JSON-LD, and llms.txt. Use the same terminology and descriptions everywhere.',
        effort: 'medium',
        impact: 'medium',
        source: 'readiness',
        rubricRef: { tool: 'readiness', category: 'crossSignalReadiness' },
      })
  }

  // Citation-based actions
  if (citation) {
    const rates =
      citation.providers
        ?.filter((p: any) => p.available)
        .map((p: any) => p.overallMentionRate) || []
    const avg =
      rates.length > 0
        ? rates.reduce((a: number, b: number) => a + b, 0) / rates.length
        : 0

    if (avg < 0.3)
      add({
        category: 'citations',
        title: 'Increase AI citation rate (currently low)',
        why: `Average mention rate is ${Math.round(avg * 100)}%. AI models are not referencing your brand in relevant queries, meaning potential customers won't discover you through AI search.`,
        how: 'Build topical authority through comprehensive content. Create definitive guides in your niche. Earn backlinks from authoritative sources. Ensure consistent NAP (name, address, phone) across the web.',
        effort: 'high',
        impact: 'high',
        source: 'citation',
        methodology: 'GEO',
        relatedUrl: 'https://www.pixelmojo.io/services/geo',
      })

    // Route the "you are not cited" result into the depth tools + a re-check
    // loop instead of leaving Citation Tracker as a terminal answer. Citation-
    // only buyers churn (they get the one answer and leave); the diagnosis +
    // re-check workflow is where retained users live.
    if (avg < 0.3)
      add({
        category: 'citations',
        title: 'Find why you are not cited, then re-check',
        why: 'A low citation rate tells you that you are not being cited, not why. The fastest path to fixing it is to diagnose the cause, apply the fix, and confirm the rate actually moves.',
        how: 'Run Source Influence to see which sources AI engines pull from when answering about your category, and Prompt SOV to see your share of voice versus competitors. Close the gaps those surface, then re-run this Citation Tracker in about a week to confirm the rate moved.',
        effort: 'medium',
        impact: 'high',
        source: 'citation',
        methodology: 'GEO',
        relatedUrl: 'https://www.pixelmojo.io/platform',
      })

    const noUrl =
      citation.providers?.filter(
        (p: any) => p.available && p.urlCitationRate === 0
      ) || []
    if (noUrl.length > 0 && avg > 0.3)
      add({
        category: 'citations',
        title: 'Improve URL citation rate',
        why: "AI models mention your brand but don't link to your website. Without URL citations, users can't easily find and visit your site from AI responses.",
        how: 'Strengthen your domain authority. Create content that AI models would want to reference as a source. Ensure your site loads fast, has good UX signals, and provides unique data or perspectives.',
        effort: 'high',
        impact: 'medium',
        source: 'citation',
      })
  }

  // Reddit-based actions
  if (reddit) {
    if (reddit.totalMentions === 0)
      add({
        category: 'community',
        title: 'Build Reddit presence for your brand',
        why: 'Zero Reddit mentions means no community signal for AI models. Reddit content is a key training data source and influences AI responses about brands.',
        how: 'Identify 3-5 relevant subreddits. Contribute genuinely helpful answers. Share expertise without self-promotion. Build organic mentions through value-first engagement.',
        effort: 'high',
        impact: 'medium',
        source: 'reddit',
      })

    if (reddit.seedingMatrix?.likelySeeded > 0)
      add({
        category: 'community',
        title: 'Address detected Reddit seeding',
        why: `${reddit.seedingMatrix.likelySeeded} mention${reddit.seedingMatrix.likelySeeded > 1 ? 's show' : ' shows'} signs of artificial promotion. Reddit users and AI models can detect this, hurting brand credibility.`,
        how: 'Audit promotional content. Shift to organic engagement: answer questions, share expertise, participate in discussions. Seeded content can result in subreddit bans and negative AI sentiment.',
        effort: 'medium',
        impact: 'high',
        source: 'reddit',
      })
  }

  // AEO-based actions
  if (aeo?.categories) {
    const cats = aeo.categories
    if (cats.speakableSchema?.score === 0)
      add({
        category: 'aeo',
        title: 'Add SpeakableSpecification to your page schema',
        why: 'Speakable schema tells voice assistants and AI Overviews which parts of your page to read aloud and cite. Without it, AI platforms guess which content to extract.',
        how: 'Add a speakable property to your JSON-LD with cssSelector targeting your headline, description, and key takeaways elements. Use Article schema for blog posts, WebPage for landing pages, or FAQPage for FAQ pages — speakable is valid on any CreativeWork.',
        effort: 'low',
        impact: 'high',
        source: 'aeo',
        methodology: 'AEO',
        relatedUrl:
          'https://www.pixelmojo.io/blogs/how-we-optimized-21-posts-for-ai-citation-aeo-implementation',
        rubricRef: { tool: 'aeo', category: 'speakableSchema' },
      })
    if (cats.answerFirstStructure?.score < 10)
      add({
        category: 'aeo',
        title: 'Restructure content for answer-first extraction',
        why: 'AI engines extract the first paragraph under each heading. If your sections open with context instead of answers, AI platforms skip your content.',
        how: 'Rephrase H2 headings as questions. Open each section with a concise definition (under 300 characters) before elaborating. Ensure valid H1 > H2 > H3 hierarchy.',
        effort: 'medium',
        impact: 'high',
        source: 'aeo',
        methodology: 'AEO',
        rubricRef: { tool: 'aeo', category: 'answerFirstStructure' },
      })
    if (cats.structuredDataQuality?.score < 12)
      add({
        category: 'structured-data',
        title: 'Complete structured data for AI citation signals',
        why: `Structured data scored ${cats.structuredDataQuality.score}/${cats.structuredDataQuality.maxScore}. Missing fields (author, dateModified, FAQPage, knowsAbout) reduce AI citation confidence.`,
        how: 'Add complete Article schema with all required fields, FAQPage for Q&A content, Organization with sameAs links, and entity-linked knowsAbout with @id references.',
        effort: 'medium',
        impact: 'high',
        source: 'aeo',
        methodology: 'GEO',
        rubricRef: { tool: 'aeo', category: 'structuredDataQuality' },
      })
    if (cats.contentFreshness?.score < cats.contentFreshness?.maxScore)
      add({
        category: 'aeo',
        title: 'Publish modification dates AI engines can read',
        why: 'Your pages carry no dateModified in Article schema and no Last-Modified header, so AI engines cannot tell whether your content is current and deprioritise it against dated competitors.',
        how: 'Add dateModified to your Article JSON-LD in ISO 8601 format, and configure your server to send a Last-Modified header. Content modified within 180 days scores highest.',
        effort: 'low',
        impact: 'medium',
        source: 'aeo',
        methodology: 'AEO',
        rubricRef: { tool: 'aeo', category: 'contentFreshness' },
      })
    if (cats.entityAuthority?.score < cats.entityAuthority?.maxScore)
      add({
        category: 'aeo',
        title: 'Link your Organization schema to authoritative profiles',
        why: 'Your Organization schema has no sameAs links, so AI models cannot connect your brand to a known entity and have no external corroboration for who you are.',
        how: 'Add sameAs to your Organization JSON-LD with your LinkedIn, Crunchbase, and Wikipedia or Wikidata URLs. Add an author Person block on content pages, and reference topical entities via knowsAbout with @id links to DefinedTerm.',
        effort: 'medium',
        impact: 'medium',
        source: 'aeo',
        methodology: 'GEO',
        rubricRef: { tool: 'aeo', category: 'entityAuthority' },
      })
    if (cats.dataExtractability?.score < 10)
      add({
        category: 'aeo',
        title: 'Add extractable data elements (tables, StatBlocks)',
        why: 'AI engines extract HTML tables verbatim and pull statistics from structured callout blocks. Prose-only content is less citable.',
        how: 'Wrap key statistics in StatBlock components with source attribution. Convert comparison data to HTML tables. Add ordered lists for step-by-step content.',
        effort: 'medium',
        impact: 'medium',
        source: 'aeo',
        methodology: 'AEO',
        relatedUrl:
          'https://www.pixelmojo.io/blogs/how-we-optimized-21-posts-for-ai-citation-aeo-implementation',
        rubricRef: { tool: 'aeo', category: 'dataExtractability' },
      })
  }

  // Answer Engine-based actions
  if (answer) {
    if (scoredAnswer && scoredAnswer.overallScore < 30)
      add({
        category: 'answer-engine',
        title: 'Improve page citation for your target question',
        why: `Citation test scored ${scoredAnswer.overallScore}/100. AI platforms are not citing your page for the tested question, which means potential customers asking this question get directed to competitors.`,
        how: 'Restructure your page to directly answer the target question in the first paragraph. Add speakable schema, StatBlocks with data points, and ensure your heading matches the question. Run the AEO Page Auditor for specific formatting gaps.',
        effort: 'medium',
        impact: 'high',
        source: 'answer',
        methodology: 'AEO',
        relatedUrl: 'https://www.pixelmojo.io/tools/answer-engine-tester',
      })
    // Only VERIFIED competitor citations justify a competitor action. Cited
    // directories, marketplaces, publications and social profiles are other
    // sources, and historical records carry no verified competitors at all.
    const answerSources = readAnswerEngineSources(answer)
    if (answerSources.verifiedCompetitorUrls.length > 0)
      add({
        category: 'answer-engine',
        title: `Analyze ${answerSources.verifiedCompetitorUrls.length} verified competitor page(s) being cited instead`,
        why: `Perplexity cited ${answerSources.verifiedCompetitorUrls.length} verified competitor URL(s) for your target question. Understanding what they include that your page does not is the fastest path to winning citations.`,
        how: `Run the AEO Page Auditor on each competitor URL to compare their scores against yours. Focus on structured data quality and data extractability gaps.`,
        effort: 'low',
        impact: 'high',
        source: 'answer',
      })
  }

  // ── New tool actions (Phase 2+3) ──────────────────────────────────────────

  const sourceInfluence = tools.sourceInfluence?.data
  const promptSov = hasUsableScore(tools.promptSov)
    ? tools.promptSov.data
    : null
  const schemaAudit = tools.schemaAudit?.data
  const hallucinationCheck = tools.hallucinationCheck?.data

  // Source Influence actions
  if (sourceInfluence) {
    if (
      !sourceInfluence.brandInSources &&
      sourceInfluence.topSources?.length > 0
    )
      add({
        category: 'source-influence',
        title: 'Create category-definitive content to become a cited source',
        why: `AI models cite ${sourceInfluence.topSources.length} sources for your category but your domain is not among them. The top cited source is ${sourceInfluence.topSources[0]?.domain}.`,
        how: 'Create comprehensive guides, original research, or definitive comparisons for your category. Study the content format of top-cited sources and build something better. Ensure strong structured data and backlink profile.',
        effort: 'high',
        impact: 'high',
        source: 'sourceInfluence',
        methodology: 'GEO',
        relatedUrl: 'https://www.pixelmojo.io/services/geo',
      })
  }

  // SOV actions
  if (promptSov) {
    const hasTarget = (promptSov.targetSOV?.mentionCount || 0) > 0
    const usableProviders =
      promptSov.usableProviders || promptSov.activeProviders || []
    if (!hasTarget)
      add({
        category: 'sov',
        title: 'Build AI share of voice from zero',
        why: 'AI providers do not mention your brand in category recommendations. You are invisible in the AI recommendation layer where many purchase decisions now start.',
        how: 'Create definitive content that directly answers "best [category] companies" queries. Build authority through backlinks and independent, third-party coverage that corroborates your position in the category.',
        effort: 'high',
        impact: 'high',
        source: 'promptSov',
        methodology: 'GEO',
        relatedUrl: 'https://www.pixelmojo.io/services/geo',
      })
    else if (promptSov.targetSOV?.rank > 3 && promptSov.competitors?.length > 0)
      add({
        category: 'sov',
        title: `Outrank ${promptSov.competitors[0].brand} in AI recommendations`,
        why: `You rank #${promptSov.targetSOV.rank} with ${promptSov.targetSOV.sovPercent}% SOV. ${promptSov.competitors[0].brand} leads at ${promptSov.competitors[0].sovPercent}%.`,
        how: `Analyze ${promptSov.competitors[0].brand}'s content strategy. Create more comprehensive, better-structured content for your category. Focus on the query types where they appear and you do not.`,
        effort: 'medium',
        impact: 'high',
        source: 'promptSov',
      })
    if (
      hasTarget &&
      promptSov.targetSOV?.providers?.length > 0 &&
      usableProviders.length > 0 &&
      promptSov.targetSOV.providers.length < usableProviders.length
    )
      add({
        category: 'sov',
        title: `Expand SOV to ${usableProviders.length - promptSov.targetSOV.providers.length} more evaluated AI provider(s)`,
        why: `You appear in ${promptSov.targetSOV.providers.join(', ')} but not in all providers that returned usable responses. Different AI engines have different knowledge bases.`,
        how: 'Review the responses from providers that did not mention you to see which competitors or sources they preferred instead. Strengthen your comparison content for the query types those providers cover -- absence usually reflects a content or authority gap, not a crawl block (check Crawl Check separately if bot access is also flagged).',
        effort: 'medium',
        impact: 'medium',
        source: 'promptSov',
      })
  }

  // Schema Audit actions
  if (schemaAudit) {
    const usesSchemaScoringV2 =
      schemaAudit.scoringMethodVersion === '2.0.0' ||
      schemaAudit.scoreBreakdown?.some(
        (category: any) => category.category === 'Foundational Coverage'
      )
    // Historical v1 results remain viewable. Keep their action attached to
    // the category that actually exists in the saved rubric; new v2 runs do
    // not treat SpeakableSpecification as a universal scored requirement.
    if (!usesSchemaScoringV2 && !schemaAudit.hasSpeakable)
      add({
        category: 'aeo',
        title: 'Add SpeakableSpecification to content pages',
        why: 'No speakable schema detected.',
        how: 'Add a speakable property to appropriate Article JSON-LD using cssSelector.',
        effort: 'low',
        impact: 'high',
        source: 'schemaAudit',
        methodology: 'AEO',
        rubricRef: { tool: 'schemaAudit', category: 'Speakable Schema' },
      })

    // These labels mirror the four contextual signals in Schema Audit v2.
    // Article and FAQPage are page-type options, never universal requirements.
    const foundTypes =
      schemaAudit.schemaTypes
        ?.filter((t: any) => t.found)
        .map((t: any) => t.type) || []
    const evaluatedTypeCount =
      schemaAudit.schemaTypes?.filter(
        (t: any) => t.found && t.evaluated !== false
      ).length || 0
    const foundationalLabels: string[] = usesSchemaScoringV2
      ? []
      : schemaAudit.schemaTypes
          ?.filter(
            (t: any) =>
              !t.found && ['Organization', 'BreadcrumbList'].includes(t.type)
          )
          .map((t: any) => t.type) || []
    if (usesSchemaScoringV2) {
      if (!foundTypes.some((type: string) => isBusinessIdentityType(type))) {
        foundationalLabels.push('business identity')
      }
      if (!foundTypes.includes('WebSite')) foundationalLabels.push('WebSite')
      if (!foundTypes.includes('BreadcrumbList'))
        foundationalLabels.push('BreadcrumbList')
    }
    if (schemaAudit.hasPageTypeSchema === false) {
      foundationalLabels.push('page type')
    }
    if (foundationalLabels.length > 0) {
      add({
        category: 'structured-data',
        title: `Add ${foundationalLabels.join(', ')} schema`,
        why: `${foundationalLabels.length} foundational or page-context signal(s) are missing.`,
        how: 'Add a business identity and WebSite entity at site level, BreadcrumbList where navigation context exists, and the page-type schema that matches each page.',
        effort: 'medium',
        impact: 'high',
        source: 'schemaAudit',
        methodology: 'GEO',
        // Each missing signal adds one real entity of one supported type.
        // Claim only the exact one-point-per-entity/type side effect, capped
        // against the current v2 saturation thresholds.
        rubricRef: usesSchemaScoringV2
          ? [
              {
                tool: 'schemaAudit',
                category: 'Foundational Coverage',
              },
              {
                tool: 'schemaAudit',
                category: 'Schema Volume',
                maxClaim:
                  Math.min(
                    10,
                    (schemaAudit.totalSchemas || 0) + foundationalLabels.length
                  ) - Math.min(10, schemaAudit.totalSchemas || 0),
              },
              {
                tool: 'schemaAudit',
                category: 'Type Diversity',
                maxClaim:
                  Math.min(10, evaluatedTypeCount + foundationalLabels.length) -
                  Math.min(10, evaluatedTypeCount),
              },
            ]
          : [
              { tool: 'schemaAudit', category: 'Key Type Coverage' },
              {
                tool: 'schemaAudit',
                category: 'Schema Volume',
                maxClaim: Math.min(10, foundationalLabels.length * 2),
              },
              {
                tool: 'schemaAudit',
                category: 'Type Diversity',
                maxClaim: Math.min(10, foundationalLabels.length * 2),
              },
            ],
        rubricWeight: foundationalLabels.length,
      })
    }
    const lowCompleteness =
      schemaAudit.schemaTypes?.filter(
        (t: any) => t.found && t.evaluated !== false && t.completeness < 50
      ) || []
    if (lowCompleteness.length > 0)
      add({
        category: 'structured-data',
        title: `Complete ${lowCompleteness.length} incomplete schema type(s)`,
        why: `${lowCompleteness.map((t: any) => `${t.type} (${t.completeness}%)`).join(', ')} have missing required or recommended fields.`,
        how: `Add missing fields: ${lowCompleteness.flatMap((t: any) => t.missingFields?.slice(0, 3) || []).join(', ')}. Use Google's Rich Results Test to validate.`,
        effort: 'medium',
        impact: 'medium',
        source: 'schemaAudit',
        rubricRef: { tool: 'schemaAudit', category: 'Field Completeness' },
        rubricWeight: lowCompleteness.length,
      })
  }

  // Hallucination actions
  if (hallucinationCheck) {
    if (
      hasUsableScore(tools.hallucinationCheck) &&
      hallucinationCheck.bySeverity?.high > 0
    ) {
      const highFindings = Array.isArray(hallucinationCheck.flags)
        ? hallucinationCheck.flags.filter(
            (flag: any) => flag.severity === 'high'
          )
        : []
      const affectedProviders = new Set(
        highFindings.map((flag: any) => String(flag.provider))
      ).size
      add({
        category: 'hallucination',
        title: 'Correct high-severity AI hallucinations about your brand',
        why: `${hallucinationCheck.bySeverity.high} high-severity hallucination ${hallucinationCheck.bySeverity.high === 1 ? 'finding was' : 'findings were'} detected across ${affectedProviders} AI ${affectedProviders === 1 ? 'response' : 'responses'}. This misinformation can reach users who ask about your brand.`,
        how: 'Update your llms.txt with accurate facts. Add complete Organization and Product schema with verified data. Ensure your About page clearly states correct founding year, products, and pricing.',
        effort: 'medium',
        impact: 'high',
        source: 'hallucinationCheck',
        methodology: 'GEO',
      })
    }
    if (
      hasUsableScore(tools.hallucinationCheck) &&
      hallucinationCheck.byCategory?.product > 0
    )
      add({
        category: 'hallucination',
        title: 'Fix wrong product/service descriptions in AI responses',
        why: `AI models describe wrong products or services. This could direct customers to the wrong solution or create false expectations.`,
        how: 'Add Product or Service schema with accurate names and descriptions. Update llms.txt product section. Ensure your homepage clearly lists current products.',
        effort: 'low',
        impact: 'high',
        source: 'hallucinationCheck',
      })
    if (
      hasUsableScore(tools.hallucinationCheck) &&
      hallucinationCheck.byCategory?.pricing > 0
    )
      add({
        category: 'hallucination',
        title: 'Correct AI pricing claims',
        why: 'AI models state incorrect pricing. Customers may form wrong expectations or choose competitors based on fabricated pricing.',
        how: 'If pricing is public, add structured pricing data to your website. If not, add a note to llms.txt that pricing is not publicly available and should not be guessed.',
        effort: 'low',
        impact: 'medium',
        source: 'hallucinationCheck',
      })
  }

  // Attach the execution-priority block. Every action generated today gets
  // priorityScore null: Radar's tools measure AI visibility, not clicks, so
  // no defensible trafficAtStake exists. See action-priority.ts — inventing
  // one would make the ranking look measured while being a guess.
  for (const action of actions) {
    action.priorityDetail = unrankedPriority(action.source)
    // Provenance from the authoritative tier map. Left undefined for an
    // unknown source rather than defaulted, so an unclassified tool can
    // never present as measured.
    action.findingType = classifyFindingType(action.source)
  }

  // ONE canonical order, shared with every consumer. The old local sort
  // (impact + effort, then overwrite priority with the index) lives on as
  // the fallback tier inside compareActions, so today's output order is
  // unchanged — but it is now one implementation instead of three.
  const sorted = sortActions(actions)
  // `priority` is kept in agreement with canonical order rather than
  // carrying a second, conflicting rank. Nothing in the repo reads it, but
  // it is part of the exported shape.
  sorted.forEach((a, i) => (a.priority = i + 1))

  return sorted
}

/* eslint-enable @typescript-eslint/no-explicit-any */
